/**
 * Call-log privacy tab — the admin-managed phone-number blocklist.
 *
 * Reads/writes GET|PUT /api/admin/call-log-privacy. Calls to/from any number on
 * this list are HIDDEN from the after-call call-log reviewer for staff + managers;
 * admins (the people editing this list) always see everything, and the live coach
 * is completely unaffected.
 *
 * UX mirrors the classifier roster editor: an "add number" input (Enter / comma /
 * the Add button) plus the current list with remove buttons, and the standard
 * dirty/saved/error Save row. The PUT returns {ok, blocked_count} (NOT the full
 * config), so we rely on invalidateQueries to refetch the authoritative list —
 * we never setQueryData the PUT response (matches the other admin tabs).
 */
import { useEffect, useState, type KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Card } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { SavedNote, ErrorNote } from "./shared";

const CALL_LOG_PRIVACY_KEY = ["admin", "call-log-privacy"] as const;
const CALL_VISIBILITY_KEY = ["admin", "call-visibility"] as const;

/** Small switch (mirrors the SettingsTab Toggle). */
function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={
        "relative inline-flex h-6 w-11 items-center rounded-full transition disabled:opacity-50 " +
        (checked ? "bg-primary" : "bg-muted")
      }
    >
      <span
        className={
          "inline-block h-4 w-4 transform rounded-full bg-white transition " +
          (checked ? "translate-x-6" : "translate-x-1")
        }
      />
    </button>
  );
}

export function CallLogPrivacyTab() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: CALL_LOG_PRIVACY_KEY,
    queryFn: ({ signal }) => api.adminGetCallLogPrivacy(signal),
  });

  // Staff call visibility permission (own-line only vs. all team calls).
  const visQuery = useQuery({
    queryKey: CALL_VISIBILITY_KEY,
    queryFn: ({ signal }) => api.adminGetCallVisibility(signal),
  });
  const visSave = useMutation({
    mutationFn: (v: boolean) => api.adminSaveCallVisibility(v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CALL_VISIBILITY_KEY });
    },
  });
  const staffSeesAll = visQuery.data?.staff_full_call_access ?? false;

  const [numbers, setNumbers] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [justSaved, setJustSaved] = useState(false);

  const data = query.data;
  useEffect(() => {
    if (data) {
      setNumbers(data.blocked_numbers);
      setJustSaved(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (blocked_numbers: string[]) =>
      api.adminSaveCallLogPrivacy(blocked_numbers),
    onSuccess: () => {
      // PUT returns {ok, blocked_count}; the GET refetch supplies the
      // authoritative (trimmed/deduped) list — do NOT setQueryData the PUT.
      void qc.invalidateQueries({ queryKey: CALL_LOG_PRIVACY_KEY });
      setJustSaved(true);
    },
  });

  if (query.isLoading) {
    return <LoadingState label="Loading call-log privacy settings…" />;
  }
  if (query.isError || !data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const saved = data.blocked_numbers;
  const dirty =
    numbers.length !== saved.length || numbers.some((n, i) => n !== saved[i]);
  const saving = save.isPending;

  function addNumber(raw: string) {
    const num = raw.trim();
    if (!num) return;
    // De-dupe on the exact string the admin typed; the server canonicalizes
    // (last-10 dedupe) on save and the refetch reflects the final list.
    if (numbers.includes(num)) {
      setDraft("");
      return;
    }
    setJustSaved(false);
    setNumbers((prev) => [...prev, num]);
    setDraft("");
  }
  function removeNumber(i: number) {
    setJustSaved(false);
    setNumbers((prev) => prev.filter((_, idx) => idx !== i));
  }
  function onDraftKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addNumber(draft);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Privacy &amp; access</h2>
          <p className="text-sm text-muted-foreground">
            Who can see which calls in the after-call reviewer.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SavedNote show={justSaved && !dirty} />
          <Button
            onClick={() => {
              setJustSaved(false);
              save.mutate(numbers);
            }}
            disabled={saving || !dirty}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <Card className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold">Staff call visibility</h2>
            <p className="text-sm text-muted-foreground">
              When ON, staff (reps) can see and review <strong>all</strong> team
              calls — transcripts, AI scores, and manager notes — not just calls
              on their own line. Reps still must self-score their <em>own</em>{" "}
              calls before the AI score unlocks, and blocked numbers below stay
              hidden. Turn OFF to limit each rep to their own line's calls.
            </p>
          </div>
          {visQuery.isLoading ? (
            <span className="text-xs text-muted-foreground">Loading…</span>
          ) : (
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {staffSeesAll ? "All team calls" : "Own line only"}
              </span>
              <Toggle
                checked={staffSeesAll}
                disabled={visSave.isPending || visQuery.isError}
                onChange={(v) => visSave.mutate(v)}
              />
            </div>
          )}
        </div>
        {visQuery.isError && (
          <p className="text-xs text-destructive">
            Couldn't load this setting. Reload to try again.
          </p>
        )}
        {visSave.isError && (
          <p className="text-xs text-destructive">Save failed — try again.</p>
        )}
      </Card>

      <Card className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold">Blocked numbers</h2>
        <p className="text-sm text-muted-foreground">
          Calls to/from these numbers are hidden from the call-log reviewer for
          staff and managers. You (admin) still see them. The live coach is not
          affected.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          {numbers.map((n, i) => (
            <span
              key={`${n}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-sm"
            >
              {n}
              <button
                type="button"
                title={`Remove ${n}`}
                onClick={() => removeNumber(i)}
                className="rounded-full text-muted-foreground transition hover:text-foreground"
              >
                ✕
              </button>
            </span>
          ))}
          {numbers.length === 0 && (
            <span className="text-sm text-muted-foreground">
              No blocked numbers yet.
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onDraftKeyDown}
            placeholder="Add a phone number…"
            className="max-w-xs rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <Button
            variant="ghost"
            className="text-xs"
            onClick={() => addNumber(draft)}
            disabled={!draft.trim()}
          >
            Add
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <ErrorNote show={save.isError} />
        </div>
      </Card>
    </div>
  );
}
